import { ShaderMaterial } from 'three';

import type { RootState } from 'src/app/store';
import { store } from 'src/app/store';
import type { Area } from 'src/data';
import { getSelectAsset } from 'src/state';
import { transitionTime } from 'src/constants';

import { addToRenderLoop } from './renderLoop';
import { loadGltf } from './gltfLoader';
import { drawOrbit } from './orbit';

const state = store.getState();
const { areas } = state.contentful;

const numberOfAreas = Object.values(areas).length;

const uniforms = {
  selected: {
    value: 0,
  },
  elapsedTime: {
    value: 0,
  },
};

const vertexShader = `
uniform float selected;
uniform float elapsedTime;
varying vec4 worldPosition;

// Some useful functions
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

//
// Description : GLSL 2D simplex noise function
//      Author : Ian McEwan, Ashima Arts
//  Maintainer : ijm
//     Lastmod : 20110822 (ijm)
//     License :
//  Copyright (C) 2011 Ashima Arts. All rights reserved.
//  Distributed under the MIT License. See LICENSE file.
//  https://github.com/ashima/webgl-noise
//
float snoise(vec2 v) {

    // Precompute values for skewed triangular grid
    const vec4 C = vec4(0.211324865405187,
                        // (3.0-sqrt(3.0))/6.0
                        0.366025403784439,
                        // 0.5*(sqrt(3.0)-1.0)
                        -0.577350269189626,
                        // -1.0 + 2.0 * C.x
                        0.024390243902439);
                        // 1.0 / 41.0

    // First corner (x0)
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v - i + dot(i, C.xx);

    // Other two corners (x1, x2)
    vec2 i1 = vec2(0.0);
    i1 = (x0.x > x0.y)? vec2(1.0, 0.0):vec2(0.0, 1.0);
    vec2 x1 = x0.xy + C.xx - i1;
    vec2 x2 = x0.xy + C.zz;

    // Do some permutations to avoid
    // truncation effects in permutation
    i = mod289(i);
    vec3 p = permute(
            permute( i.y + vec3(0.0, i1.y, 1.0))
                + i.x + vec3(0.0, i1.x, 1.0 ));

    vec3 m = max(0.5 - vec3(
                        dot(x0,x0),
                        dot(x1,x1),
                        dot(x2,x2)
                        ), 0.0);

    m = m*m ;
    m = m*m ;

    // Gradients:
    //  41 pts uniformly over a line, mapped onto a diamond
    //  The ring size 17*17 = 289 is close to a multiple
    //      of 41 (41*7 = 287)

    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;

    // Normalise gradients implicitly by scaling m
    // Approximation of: m *= inversesqrt(a0*a0 + h*h);
    m *= 1.79284291400159 - 0.85373472095314 * (a0*a0+h*h);

    // Compute final noise value at P
    vec3 g = vec3(0.0);
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * vec2(x1.x,x2.x) + h.yz * vec2(x1.y,x2.y);
    return 130.0 * dot(m, g);
}

void main() {
  worldPosition = modelMatrix * vec4(position, 1.0);
  vec3 p = vec3(position.x, position.y, position.z);

  float originalY = p.y;
  p.y += selected * 4. * p.y; // ranges from p.y -> 5 * p.y

  if (originalY > 2.) {
    float scaledTime = elapsedTime / 3.;
    float spaceScale = 30.;
    vec2 noisePosition = vec2(p.x / spaceScale, p.z / spaceScale) + vec2(scaledTime, scaledTime);
    p.y += selected * snoise(noisePosition) * 10.;
  }

  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}`

const fragmentShader = `
varying vec4 worldPosition;

void main() {
  float val = (worldPosition.y / 10.) * 0.75;
  gl_FragColor = vec4(0, 0, 0, val);
}
`

const shaderMaterial = new ShaderMaterial({
  uniforms,
  transparent: true,
  // side: DoubleSide,
  vertexShader,
  fragmentShader,
});

const materials: Record<string, ShaderMaterial> = {};
const materialsArray: ShaderMaterial[] = [];

function loadGeometry(area: Area, state: RootState, numberOfModels: number) {
  const assetLink = area.fields.model['en-US'];
  const asset = getSelectAsset(assetLink.sys.id)(state);
  const fileUrl = asset.fields.file['en-US'].url;
  const material = shaderMaterial.clone();
  loadGltf(fileUrl, material, numberOfModels);

  materials[area.sys.id] = material;
  materialsArray.push(material);
}

for (const areaId in areas) {
  const area = areas[areaId];

  loadGeometry(area, state, numberOfAreas);
  drawOrbit(area, store);
}

addToRenderLoop(update);

let transitioning = false;
let progress = 0;
let previousAreaId: string | undefined;
let currentAreaId = state.navigation.currentAreaId;
onStateChange();

store.subscribe(onStateChange);

function onStateChange() {
  const state = store.getState();

  if (state.navigation.currentAreaId !== currentAreaId) {
    updateAreaId(state);
  }
}

function updateAreaId(state: RootState) {
  previousAreaId = currentAreaId;
  currentAreaId = state.navigation.currentAreaId 
  transitioning = true;
}

function update(delta: number, elapsedTime: number) {
  if (transitioning) progress += Math.min(delta / transitionTime, 1);

  materialsArray.forEach((material) => {
    material.uniforms.elapsedTime.value = elapsedTime;
  });

  if (transitioning) {
    if (previousAreaId) materials[previousAreaId].uniforms.selected.value = 1. - progress;
    materials[currentAreaId].uniforms.selected.value = progress;
  }

  if (progress >= 1) {
    progress = 0;
    transitioning = false;
  }
}
